// 🔥 용량/연결성 Chip 그룹 컴포넌트
// [배경] 모델 카드에서 해당 모델의 모든 용량/연결성 옵션을 Chip 형태로 표시
//        태블릿/스마트폰/웨어러블 공통 사용

import React, { memo } from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import {
  Storage as StorageIcon,
  Wifi as WifiIcon,
  SignalCellularAlt as CellularIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

// 📌 Props 인터페이스
export interface VariantInfo {
  storage: string;
  connectivity: string;
  hasPrice: boolean;
  priceA?: number; // A등급 가격 (미리보기용)
}

export interface VariantChipGroupProps {
  /** 용량/연결성 조합 목록 */
  variants: VariantInfo[];
  /** 디바이스 타입 */
  deviceType: 'tablet' | 'smartphone' | 'wearable';
  /** 최대 표시 개수 (초과 시 +N 표시) */
  maxDisplay?: number;
  /** 선택된 용량 */
  selectedStorage?: string;
  /** 용량 클릭 핸들러 */
  onStorageClick?: (storage: string) => void;
  /** 컴팩트 모드 (작은 Chip) */
  compact?: boolean;
}

/**
 * 🔥 용량/연결성 Chip 그룹 컴포넌트
 * 
 * @description
 * 모델의 모든 용량 옵션을 Chip으로 시각화합니다.
 * - 가격 설정 여부에 따라 색상 구분
 * - 태블릿: 연결성까지 표시 (Wi-Fi / Cellular)
 * - 스마트폰/웨어러블: 용량만 표시
 */
const VariantChipGroup: React.FC<VariantChipGroupProps> = ({
  variants,
  deviceType,
  maxDisplay = 6,
  selectedStorage,
  onStorageClick,
  compact = false
}) => {
  // 📊 용량별로 그룹핑 (같은 용량의 여러 연결성 합치기)
  const storageGroups = React.useMemo(() => {
    const groups = new Map<string, {
      storage: string;
      connectivities: string[];
      hasPriceAll: boolean;
      hasPriceAny: boolean;
      priceA?: number;
    }>();

    variants.forEach(v => {
      const existing = groups.get(v.storage);
      if (existing) {
        if (!existing.connectivities.includes(v.connectivity)) {
          existing.connectivities.push(v.connectivity);
        }
        existing.hasPriceAll = existing.hasPriceAll && v.hasPrice;
        existing.hasPriceAny = existing.hasPriceAny || v.hasPrice;
        if (!existing.priceA && v.priceA) {
          existing.priceA = v.priceA;
        }
      } else {
        groups.set(v.storage, {
          storage: v.storage,
          connectivities: [v.connectivity],
          hasPriceAll: v.hasPrice,
          hasPriceAny: v.hasPrice,
          priceA: v.priceA
        });
      }
    });

    // 용량 순서 정렬 (숫자 기준)
    return Array.from(groups.values()).sort((a, b) => {
      const aNum = parseInt(a.storage.replace(/[^0-9]/g, '')) || 0;
      const bNum = parseInt(b.storage.replace(/[^0-9]/g, '')) || 0;
      return aNum - bNum;
    });
  }, [variants]);

  // 📊 표시할 그룹 (maxDisplay 기준)
  const displayGroups = storageGroups.slice(0, maxDisplay);
  const hiddenCount = storageGroups.length - displayGroups.length;

  // 🎨 연결성 아이콘 반환
  const getConnectivityIcon = (connectivity: string) => {
    if (connectivity.includes('Cellular') || connectivity.includes('5G')) {
      return <CellularIcon sx={{ fontSize: 12 }} />;
    }
    if (connectivity === 'Wi-Fi') {
      return <WifiIcon sx={{ fontSize: 12 }} />;
    }
    return null;
  };

  // 🎨 Chip 색상 결정
  const getChipColor = (group: typeof storageGroups[0]): 'success' | 'warning' | 'default' => {
    if (group.hasPriceAll) return 'success';
    if (group.hasPriceAny) return 'warning';
    return 'default';
  };

  // 🎨 툴팁 내용 생성
  const getTooltipContent = (group: typeof storageGroups[0]) => {
    const lines = [];
    
    if (deviceType === 'tablet') {
      lines.push(`연결성: ${group.connectivities.join(', ')}`);
    }
    
    if (group.priceA) {
      lines.push(`A등급: ${group.priceA.toLocaleString()}원`);
    }
    
    if (!group.hasPriceAll) {
      lines.push('⚠️ 일부 구성 가격 미설정');
    }
    
    return lines.join('\n');
  };

  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
      {displayGroups.map((group) => {
        const chipColor = getChipColor(group);
        const isSelected = selectedStorage === group.storage;
        
        return (
          <Tooltip
            key={group.storage}
            title={getTooltipContent(group)}
            arrow
            placement="top"
          >
            <Chip
              size={compact ? 'small' : 'medium'}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" fontWeight={600}>
                    {group.storage}
                  </Typography>
                  {/* 태블릿: 연결성 표시 (여러 개면 아이콘으로) */}
                  {deviceType === 'tablet' && group.connectivities.length > 1 && (
                    <Box sx={{ display: 'flex', gap: 0.25 }}>
                      <WifiIcon sx={{ fontSize: 10, opacity: 0.7 }} />
                      <CellularIcon sx={{ fontSize: 10, opacity: 0.7 }} />
                    </Box>
                  )}
                </Box>
              }
              color={chipColor}
              variant={isSelected ? 'filled' : (chipColor === 'default' ? 'outlined' : 'filled')}
              onClick={onStorageClick ? () => onStorageClick(group.storage) : undefined}
              icon={
                group.hasPriceAll ? (
                  <CheckIcon sx={{ fontSize: compact ? 12 : 14 }} />
                ) : !group.hasPriceAny ? (
                  <WarningIcon sx={{ fontSize: compact ? 12 : 14 }} />
                ) : undefined
              }
              sx={{
                cursor: onStorageClick ? 'pointer' : 'default',
                fontSize: compact ? '0.7rem' : '0.75rem',
                height: compact ? 22 : 26,
                fontWeight: 600,
                ...(isSelected && {
                  border: '2px solid',
                  borderColor: 'primary.main'
                }),
                '&:hover': onStorageClick ? {
                  transform: 'scale(1.05)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                } : {}
              }}
            />
          </Tooltip>
        );
      })}
      
      {/* 숨겨진 항목 표시 */}
      {hiddenCount > 0 && (
        <Chip
          size={compact ? 'small' : 'medium'}
          label={`+${hiddenCount}`}
          variant="outlined"
          sx={{
            fontSize: compact ? '0.7rem' : '0.75rem',
            height: compact ? 22 : 26,
            fontWeight: 600,
            bgcolor: 'grey.100'
          }}
        />
      )}
    </Box>
  );
};

// 🔧 displayName 설정
VariantChipGroup.displayName = 'VariantChipGroup';

// 🔥 React.memo로 최적화
export default memo(VariantChipGroup);
